import { hash } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBanner,
  PrismButton,
  PrismCard,
  PrismInput,
  PrismMessage,
  PrismTable,
} from '@mylighthouse/prism-ember';
import type { CustomEmberTableColumn } from '@mylighthouse/prism-ember/types/components/prism/table';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import GeneralNavigationIntercomHelpLink from 'lumen/components/general/navigation/intercom-help-link';
import SettingsCurrencyConversionDefaultCurrencyPicker from 'lumen/components/settings/currency-conversion/default-currency-picker';
import SettingsCurrencyConversionModalsCreateProfile from 'lumen/components/settings/currency-conversion/modals/create-profile';
import type { PortfolioAccess } from 'lumen/components/settings/currency-conversion/profile';
import SettingsCurrencyConversionTablesCellsTdTitle from 'lumen/components/settings/currency-conversion/tables/cells/td-title';
import SettingsCurrencyConversionTablesCellsTdWithActions from 'lumen/components/settings/currency-conversion/tables/cells/td-with-actions';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import type {
  Currency,
  CurrencyConversionExchangeRatesProfile,
} from 'lumen/models';
import { timeout } from 'lumen/utils/waiters';

type Row = {
  [COLUMNS.TITLE]: string;
  [COLUMNS.CREATED]: number;
  [COLUMNS.CREATED_DISPLAY]: string;
  [COLUMNS.LAST_EDITED]: number;
  [COLUMNS.LAST_EDITED_DISPLAY]: string;
  [COLUMNS.ACTIONS]: CurrencyConversionExchangeRatesProfile;
  profile: CurrencyConversionExchangeRatesProfile;
};

const COLUMNS = {
  TITLE: 'title',
  CREATED: 'created',
  CREATED_DISPLAY: 'createdDisplay',
  LAST_EDITED: 'lastEdited',
  LAST_EDITED_DISPLAY: 'lastEditedDisplay',
  ACTIONS: 'actions',
} as const;

interface SettingsCurrencyConversionLandingPageSignature {
  Args: {
    baseCurrencies: Currency[];
    exchangeRatesProfiles: CurrencyConversionExchangeRatesProfile[];
    isBannerDismissed: boolean;
    isLoading: boolean;
    isReadOnly?: boolean;
    onDismissBanner: () => void;
    onNavigateToPortfolio?: () => void;
    portfolioAccess?: PortfolioAccess | null;
  };
}

export default class SettingsCurrencyConversionLandingPage extends Component<SettingsCurrencyConversionLandingPageSignature> {
  @service declare intl: Services['intl'];

  @tracked isCreateProfileModalOpen = false;
  @tracked searchInputValue = '';

  initialSorts = [
    {
      valuePath: COLUMNS.TITLE,
      isAscending: true,
    },
  ];

  onSearchInputTask = task(
    { restartable: true },
    async (event: Event): Promise<void> => {
      await timeout();
      this.searchInputValue = (event.target as HTMLInputElement)?.value;
    },
  );

  get bannerMessage(): string {
    if (this.args.isReadOnly) {
      return this.intl.t(
        'settings.currency_conversion.banner.incomplete_profiles.read_only',
      );
    }
    return this.intl.t(
      'settings.currency_conversion.banner.incomplete_profiles',
    );
  }

  get columns(): CustomEmberTableColumn[] {
    return [
      {
        name: this.intl.t('settings.currency_conversion.title'),
        valuePath: COLUMNS.TITLE,
        tdComponent: SettingsCurrencyConversionTablesCellsTdTitle,
        isReadOnly: this.args.isReadOnly ?? false,
      },
      {
        name: this.intl.t('settings.currency_conversion.columns.created'),
        valuePath: COLUMNS.CREATED,
        displayValuePath: COLUMNS.CREATED_DISPLAY,
        textAlign: 'left' as const,
        minWidth: 100,
        width: 150,
      },
      {
        name: this.intl.t('settings.currency_conversion.columns.last_edited'),
        valuePath: COLUMNS.LAST_EDITED,
        displayValuePath: COLUMNS.LAST_EDITED_DISPLAY,
        textAlign: 'left' as const,
        minWidth: 100,
        width: 150,
      },
      {
        name: '',
        valuePath: COLUMNS.ACTIONS,
        textAlign: 'right' as const,
        isSortable: false,
        isReadOnly: this.args.isReadOnly ?? false,
        tdComponent: SettingsCurrencyConversionTablesCellsTdWithActions,
      },
    ];
  }

  get emptyState(): { description?: string; title: string } {
    if (this.searchInputValue) {
      return {
        title: this.intl.t(
          'settings.currency_conversion.profiles.empty_state.no_results.title',
        ),
        description: this.intl.t(
          'settings.currency_conversion.profiles.empty_state.no_results.description',
        ),
      };
    }

    const title = this.intl.t(
      'settings.currency_conversion.profiles.empty_state.title',
    );

    if (this.args.isReadOnly) {
      if (this.args.portfolioAccess) {
        return {
          title,
          description: this.intl.t(
            'settings.currency_conversion.profiles.empty_state.hotel_editor_description',
          ),
        };
      }
      return {
        title,
        description: this.intl.t(
          'settings.currency_conversion.profiles.empty_state.read_only_description',
        ),
      };
    }

    return {
      title,
      description: this.intl.t(
        'settings.currency_conversion.profiles.empty_state.description',
      ),
    };
  }

  get filteredRows(): Row[] {
    const searchInputValueLowercase = this.searchInputValue
      .toLowerCase()
      .trim();

    return this.rows.filter((row) => {
      const titleLowercase = row.title.toLowerCase().trim();

      return titleLowercase.includes(searchInputValueLowercase);
    });
  }

  get rows(): Row[] {
    if (!this.args.exchangeRatesProfiles) {
      return [];
    }

    return this.args.exchangeRatesProfiles.map((profile) => ({
      [COLUMNS.TITLE]: profile.title,
      [COLUMNS.CREATED]: profile.createdAt.valueOf(),
      [COLUMNS.CREATED_DISPLAY]: profile.createdAtFromNow,
      [COLUMNS.LAST_EDITED]: profile.updatedAt.valueOf(),
      [COLUMNS.LAST_EDITED_DISPLAY]: profile.updatedAtFromNow,
      [COLUMNS.ACTIONS]: profile,
      profile,
    }));
  }

  get showGoToPortfolio(): boolean {
    return (
      !!this.args.isReadOnly &&
      !!this.args.portfolioAccess &&
      !this.searchInputValue
    );
  }

  get showProfileWarnings(): boolean {
    if (this.args.isBannerDismissed) {
      return false;
    }

    return this.args.exchangeRatesProfiles.some(
      (profile) =>
        profile.isMissingPortfolioCurrencies ||
        profile.hasNoExchangeRates ||
        profile.hasInvalidRates,
    );
  }

  @action
  closeCreateProfileModal(): void {
    this.isCreateProfileModalOpen = false;
  }

  @action
  openCreateProfileModal(): void {
    this.isCreateProfileModalOpen = true;
  }

  @action
  validateProfileTitleUniqueness(title: string): boolean {
    const existingTitles = new Set(
      this.args.exchangeRatesProfiles.map((e) => e.title.trim().toLowerCase()),
    );
    return !existingTitles.has(title.trim().toLowerCase());
  }

  <template>
    <SettingsPageTemplate
      @description={{t "settings.currency_conversion.page_description"}}
      @title={{t "general.currency"}}
      @type="container"
    >
      <:link>
        <GeneralNavigationIntercomHelpLink
          @articleLink="en/articles/13689943-currency-in-lighthouse"
          @iconName="book"
          @linkText={{t "general.learn_more"}}
          @type="text-and-icon"
        />
      </:link>
      <:default>
        <div class="flex flex-col gap-400">
          <SettingsCurrencyConversionDefaultCurrencyPicker
            @isLoading={{@isLoading}}
            @isReadOnly={{@isReadOnly}}
            @portfolioCurrencies={{@baseCurrencies}}
          />

          {{#if this.showProfileWarnings}}
            <PrismBanner
              @bannerMessage={{this.bannerMessage}}
              @bannerStyle="warning"
              @close={{@onDismissBanner}}
              class="mb-200"
            />
          {{/if}}

          <PrismCard @contentLayout="noPadding" class="overflow-hidden">
            <:header as |Header|>
              <Header.title>
                {{t "settings.currency_conversion.profiles.heading"}}
              </Header.title>
            </:header>

            <:headerAfter>
              <div class="flex gap-200">
                <PrismInput
                  @iconAfter={{hash name="search"}}
                  @name="search"
                  @onInput={{this.onSearchInputTask.perform}}
                  @placeholder={{t "settings.currency_conversion.search"}}
                  @removePadding={{true}}
                  @type="text"
                  @value={{this.searchInputValue}}
                />

                {{#unless @isReadOnly}}
                  <PrismButton
                    @iconBefore="plus"
                    @label={{t "settings.currency_conversion.create_profile"}}
                    @onClick={{this.openCreateProfileModal}}
                    data-test-id="currency-conversion-create-profile-button"
                  />
                {{/unless}}
              </div>
            </:headerAfter>

            <:content>
              <PrismTable
                @columns={{this.columns}}
                @enableAutoSortsUpdate={{true}}
                @fillMode="first-column"
                @initialSorts={{this.initialSorts}}
                @isLoading={{@isLoading}}
                @nrOfLoadingRows={{2}}
                @removeTableBorder={{true}}
                @rows={{this.filteredRows}}
                @widthConstraint="eq-container"
              >
                <:emptyState>
                  <div
                    class="flex w-full items-center justify-center"
                    data-test-id="no-profiles-empty-state"
                  >
                    {{#if this.showGoToPortfolio}}
                      <PrismMessage
                        @description={{this.emptyState.description}}
                        @illustration="100x100_empty"
                        @title={{this.emptyState.title}}
                      >
                        <:primaryAction>
                          <PrismButton
                            @iconAfter="arrow-right"
                            @label={{t
                              "settings.currency_conversion.profiles.empty_state.go_to_portfolio"
                            }}
                            @onClick={{@onNavigateToPortfolio}}
                            data-test-id="currency-conversion-navigate-to-portfolio"
                          />
                        </:primaryAction>
                      </PrismMessage>
                    {{else}}
                      <PrismMessage
                        @description={{this.emptyState.description}}
                        @illustration="100x100_empty"
                        @title={{this.emptyState.title}}
                      />
                    {{/if}}
                  </div>
                </:emptyState>
              </PrismTable>
            </:content>
          </PrismCard>
        </div>

        {{#if this.isCreateProfileModalOpen}}
          <SettingsCurrencyConversionModalsCreateProfile
            @isTitleUnique={{this.validateProfileTitleUniqueness}}
            @onClose={{this.closeCreateProfileModal}}
          />
        {{/if}}
      </:default>
    </SettingsPageTemplate>
  </template>
}
